/** /settings/agent-runs — audit trail for shared-loop invocations
 *  (W076.5).
 *
 *  Every `runAgentLoop({...})` writes one row to agent_runs with
 *  feature_id, prompt version, provider + model, latency, status, and
 *  a one-line result summary. This page is a thin viewer over the
 *  /api/settings/agent-runs and /stats endpoints. No em dashes.
 */
import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

interface RunRow {
  id: string;
  feature_id: string;
  prompt_version: number | null;
  status: string;
  provider: string | null;
  model: string | null;
  latency_ms: number | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  result_summary: string | null;
  error: string | null;
  started_at: string;
  ended_at: string | null;
}

interface RunSummary {
  total: number;
  ok: number;
  error: number;
}

interface FeatureStat {
  feature_id: string;
  total: number;
  ok_count: number;
  error_count: number;
  avg_latency_ms: number | null;
  last_at: string | null;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function fmtTs(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso.endsWith("Z") ? iso : `${iso}Z`);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function SettingsAgentRuns() {
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [summary, setSummary] = useState<RunSummary>({ total: 0, ok: 0, error: 0 });
  const [stats, setStats] = useState<FeatureStat[]>([]);
  const [featureFilter, setFeatureFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (featureFilter) q.set("featureId", featureFilter);
      if (statusFilter) q.set("status", statusFilter);
      q.set("limit", "200");
      const [rRes, sRes] = await Promise.all([
        fetch(`/api/settings/agent-runs?${q.toString()}`, { credentials: "same-origin", cache: "no-store" }),
        fetch(`/api/settings/agent-runs/stats`, { credentials: "same-origin", cache: "no-store" }),
      ]);
      if (!rRes.ok) {
        setErr(`/api/settings/agent-runs returned ${rRes.status}`);
        return;
      }
      const rBody = (await rRes.json()) as { runs: RunRow[]; summary: RunSummary };
      const sBody = sRes.ok ? ((await sRes.json()) as { stats: FeatureStat[] }) : { stats: [] };
      setRuns(rBody.runs);
      setSummary(rBody.summary);
      setStats(sBody.stats);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [featureFilter, statusFilter]);

  const featureOptions = Array.from(new Set(stats.map((s) => s.feature_id))).sort();

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 text-zinc-200">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Agent runs</h1>
          <p className="text-sm text-zinc-400">
            Audit trail for every shared-loop invocation (W076.5). One row per call.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-1 rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-300 hover:bg-zinc-800"
        >
          <RefreshCw className="h-3 w-3" /> refresh
        </button>
      </div>

      {err && (
        <div className="mb-4 rounded border border-rose-700 bg-rose-950 px-3 py-2 text-sm text-rose-200">{err}</div>
      )}

      <div className="mb-4 grid grid-cols-3 gap-3 text-sm">
        <div className="rounded border border-zinc-800 bg-zinc-950 px-3 py-2">
          <div className="text-xs uppercase tracking-wide text-zinc-500">total (this view)</div>
          <div className="text-xl text-zinc-100">{summary.total}</div>
        </div>
        <div className="rounded border border-zinc-800 bg-zinc-950 px-3 py-2">
          <div className="text-xs uppercase tracking-wide text-zinc-500">ok</div>
          <div className="text-xl text-emerald-300">{summary.ok}</div>
        </div>
        <div className="rounded border border-zinc-800 bg-zinc-950 px-3 py-2">
          <div className="text-xs uppercase tracking-wide text-zinc-500">error</div>
          <div className={`text-xl ${summary.error > 0 ? "text-rose-300" : "text-zinc-500"}`}>{summary.error}</div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
        <label className="text-zinc-500">feature:</label>
        <select
          className="rounded border border-zinc-800 bg-zinc-900 px-2 py-1 text-zinc-200"
          value={featureFilter}
          onChange={(e) => setFeatureFilter(e.target.value)}
        >
          <option value="">all</option>
          {featureOptions.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
        <label className="text-zinc-500">status:</label>
        <select
          className="rounded border border-zinc-800 bg-zinc-900 px-2 py-1 text-zinc-200"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">all</option>
          <option value="ok">ok</option>
          <option value="error">error</option>
          <option value="started">started</option>
        </select>
      </div>

      <details className="mb-6 rounded border border-zinc-800 bg-zinc-950 px-3 py-2">
        <summary className="cursor-pointer text-sm text-zinc-300">
          Per-feature stats (last 7 days) — {stats.length} features
        </summary>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-zinc-500">
                <th className="py-1 pr-3">feature</th>
                <th className="py-1 pr-3">total</th>
                <th className="py-1 pr-3">ok</th>
                <th className="py-1 pr-3">error</th>
                <th className="py-1 pr-3">avg latency</th>
                <th className="py-1 pr-3">last call</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => (
                <tr key={s.feature_id} className="border-b border-zinc-900">
                  <td className="py-1 pr-3 font-mono text-zinc-200">{s.feature_id}</td>
                  <td className="py-1 pr-3">{s.total}</td>
                  <td className="py-1 pr-3 text-emerald-300">{s.ok_count}</td>
                  <td className={`py-1 pr-3 ${s.error_count > 0 ? "text-rose-300" : ""}`}>{s.error_count}</td>
                  <td className="py-1 pr-3">{s.avg_latency_ms != null ? `${Math.round(s.avg_latency_ms)} ms` : ""}</td>
                  <td className="py-1 pr-3 text-zinc-500">{fmtTs(s.last_at)}</td>
                </tr>
              ))}
              {stats.length === 0 && (
                <tr><td colSpan={6} className="py-2 text-zinc-500">no runs in the last 7 days yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </details>

      {loading && <div className="text-sm text-zinc-500">Loading...</div>}

      <div className="overflow-x-auto rounded border border-zinc-800">
        <table className="w-full text-xs">
          <thead className="bg-zinc-900 text-left text-zinc-500">
            <tr>
              <th className="px-2 py-1">started</th>
              <th className="px-2 py-1">feature</th>
              <th className="px-2 py-1">status</th>
              <th className="px-2 py-1">model</th>
              <th className="px-2 py-1">latency</th>
              <th className="px-2 py-1">tokens (in / out)</th>
              <th className="px-2 py-1">summary / error</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-t border-zinc-900 align-top">
                <td className="px-2 py-1 text-zinc-500">{fmtTs(r.started_at)}</td>
                <td className="px-2 py-1 font-mono text-zinc-200">{r.feature_id}</td>
                <td className={`px-2 py-1 ${
                  r.status === "ok" ? "text-emerald-300" :
                  r.status === "error" ? "text-rose-300" : "text-zinc-400"
                }`}>{r.status}</td>
                <td className="px-2 py-1 text-zinc-400">
                  {r.provider ? `${r.provider}/${r.model}` : ""}
                </td>
                <td className="px-2 py-1 text-zinc-400">{r.latency_ms != null ? `${r.latency_ms} ms` : ""}</td>
                <td className="px-2 py-1 text-zinc-400">
                  {r.prompt_tokens != null || r.completion_tokens != null
                    ? `${r.prompt_tokens ?? "?"} / ${r.completion_tokens ?? "?"}`
                    : ""}
                </td>
                <td className="px-2 py-1 text-zinc-300">
                  {r.error
                    ? <span className="text-rose-300">{r.error}</span>
                    : <span>{r.result_summary ?? ""}</span>}
                </td>
              </tr>
            ))}
            {!loading && runs.length === 0 && (
              <tr><td colSpan={7} className="px-2 py-3 text-zinc-500">No runs yet. Migrated call sites populate this as they fire.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
